
import { catchError, distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Payroll, PayrollStatusCounts } from '../../../services/payroll.model';
import { PayrollService } from '../../../services/payroll.service';
import { Product, Products } from '../../../services/product.model';
import { PayrollInstanceStatus } from '../../../shared/enums/smarthub-enums';
import { PayrollCounts } from '../../../services/payroll-counts.model';
import { DashboardFilters } from '../../dashboard/filters/dashboard-filters.model';
import { forkJoin } from 'rxjs';
import { ProgressStates } from '../progress-states.model';
import * as _ from 'lodash';
import { FiltersService } from '../../dashboard/filters/filters.service';
import { BaseObserver } from '../../../shared/baseobserver';
import { Util } from '../../../../util';

interface PortionRollup {
  totalAmount: number;
  totalCount: number;
}

class ProductPayrollInfo {
  product: Product;
  totalAmount: number;
  totalCount: number;
  counts: PayrollCounts;
  payrolls: Payroll[];
  payrollsExpanded = new Map<Payroll, boolean>();
  portionRollups: PortionRollup[];

  constructor(product: Product) {
    this.product = product;
  }
}

class PayrollPortionProfileSubtotal {
  labelKey: string;
  countProperty: string;
  totalProperty: string;
}

class PayrollPortionProfile {
  titleKey: string;
  countKey: string;
  totalCountKey: string;
  notesKey: string;
  totals: PayrollPortionProfileSubtotal;
  subtotals: PayrollPortionProfileSubtotal[];
  totalCountProperty: string;
  isInvalid: boolean;
}

@Component({
  selector: 'ssg-at-a-glance-payroll',
  templateUrl: './at-a-glance-payroll.component.html',
  styleUrls: ['../at-a-glance.component.less']
})
export class AtAGlancePayrollComponent extends BaseObserver implements OnInit, OnDestroy {
  stateChanged = new EventEmitter<any>();
  @Output() progressStatesChanged = new EventEmitter<ProgressStates>();
  private _allExpanded = false;
  private _sectionExpanded = false;

  loadingPayrolls = false;
  loadingCounts = false;
  productInfo: ProductPayrollInfo = new ProductPayrollInfo(Products.EmployeePay);
  portionProfiles: PayrollPortionProfile[] = [];
  totalPayrollCounts: PayrollCounts = new PayrollCounts();

  get hasData(): boolean {
    return !!( (this.productInfo.payrolls && this.productInfo.payrolls.length)
            || (this.totalPayrollCounts && this.totalPayrollCounts.expectedPayrollCount) );
  }

  constructor(
    private readonly payrollService: PayrollService,
    private readonly filtersService: FiltersService,
  ) {
    super();
  }

  ngOnInit() {
    this._initLoaders();
    this._initPortionProfiles();
    this.sectionExpanded = true;
  }

  ngOnDestroy() {
    this._clearPayrolls();
  }

  getPaygroupTypeKey(payroll: Payroll) {
    if (payroll.paySequence === 1) {
      return 'AT_A_GLANCE.PAYGROUP_TYPE_REGULAR';
    }

    return 'AT_A_GLANCE.PAYGROUP_TYPE_OFFCYCLE';
  }

  isPayrollPosting(payroll: Payroll) {
    return payroll.status === PayrollInstanceStatus.VerifiedWithCriticalErrors
        || payroll.status === PayrollInstanceStatus.VerifiedWithNotCollectableErrors
        || payroll.status === PayrollInstanceStatus.VerifiedWithNotPayableErrors
        || payroll.status === PayrollInstanceStatus.WaitingForClose
        || payroll.status === PayrollInstanceStatus.Processing;
  }

  isPayrollClosed(payroll: Payroll) {
    return payroll.status === PayrollInstanceStatus.Closed
        || payroll.status === PayrollInstanceStatus.Processed;
  }

  private _initLoaders() {
    this.filtersService.getFilters()
      .pipe(
        filter((filt) => filt.payDate !== Util.MIN_DATE),
        distinctUntilChanged((a, b) => DashboardFilters.compareFilters(a, b)),
        takeUntil(this.destroy$)
      ).subscribe((filters: DashboardFilters) => {
          this._clearPayrolls();
          forkJoin([
            this.payrollService.getPayrolls(
              Products.EmployeePay, filters.payDate.toDate(), filters.getPaySequences(), filters.payGroups),
            this.payrollService.getPayrollCounts(
              Products.EmployeePay, filters.payDate.toDate(), filters.getPaySequences(), filters.payGroups)
            ]
          ).pipe(
            catchError(error => error))
            .pipe(takeUntil(this.destroy$))
            .subscribe((employeePayData: string | [Payroll[], PayrollCounts]) => {
              if (typeof employeePayData === 'string') {
                // TODO: console.error(employeePayData);
              } else {
                this._processPayrolls(Products.EmployeePay, employeePayData[0], employeePayData[1]);
            }
          });
    });
  }

  private _clearPayrolls() {
    this.productInfo = new ProductPayrollInfo(Products.EmployeePay);
    this.totalPayrollCounts = new PayrollCounts();
    this.loadingCounts = false;
  }

  private _processPayrolls(product: Product, payrolls: Payroll[], counts: PayrollCounts) {
    if (payrolls && payrolls.length) {
      const info = new ProductPayrollInfo(product);

      info.payrolls = _.sortBy(payrolls, ['payGroup', 'paySequence']);
      info.totalAmount = 0;
      info.totalCount = 0;
      info.portionRollups = [];
      this.portionProfiles.forEach(() => {
        info.portionRollups.push({ totalAmount: 0, totalCount: 0 });
      });
      for (const payroll of payrolls) {
        if (!payroll.payStatusCounts) {
          payroll.payStatusCounts = new PayrollStatusCounts();
        }

        if (this.isPayrollClosed(payroll)) {
          info.totalAmount += (payroll.payStatusCounts.collectablePaysAmount || 0);
          info.totalCount += (payroll.payStatusCounts.collectablePaysCount || 0);

          for (let j = 0; j < this.portionProfiles.length; j++) {
            info.portionRollups[j].totalAmount += (payroll.payStatusCounts[this.portionProfiles[j].totals.totalProperty] || 0);
            info.portionRollups[j].totalCount += (payroll.payStatusCounts[this.portionProfiles[j].totals.countProperty] || 0);
          }
        }
      }

      info.counts = counts;

      this.productInfo = info;
      this.loadingCounts = true;
    }

    if (counts) {
      this.totalPayrollCounts = new PayrollCounts();
      Object.keys(counts).forEach(key => {
        if (typeof counts[key] === 'number') {
          this.totalPayrollCounts[key] = (this.totalPayrollCounts[key] || 0) + counts[key];
        }
      });
    }

    const hasStarted = this.totalPayrollCounts.closedPayrollCount > 0 || this.totalPayrollCounts.inProgressPayrollCount > 0;
    const hasFinished = this.totalPayrollCounts.closedPayrollCount >= this.totalPayrollCounts.expectedPayrollCount;
    this.progressStatesChanged.emit({
      complete: hasStarted && hasFinished,
      inProgress: hasStarted
    });
  }

  private _initPortionProfiles() {
    this.portionProfiles.push({
      titleKey: 'AT_A_GLANCE.PORTION_CHECK_TITLE',
      countKey: 'AT_A_GLANCE.PORTION_CHECK_COUNT',
      totalCountKey: null,
      notesKey: 'AT_A_GLANCE.PAYROLL_CLOSED_DETAILS_NOTES',
      totals: {
        labelKey: null,
        countProperty: 'collectableCheckPaysCount',
        totalProperty: 'collectableCheckPaysAmount'
      },
      subtotals: [],
      totalCountProperty: null,
      isInvalid: false
    });
    this.portionProfiles.push({
      titleKey: 'AT_A_GLANCE.PORTION_DD_TITLE',
      countKey: 'AT_A_GLANCE.PORTION_DD_COUNT',
      totalCountKey: 'AT_A_GLANCE.PORTION_DD_TOTALCOUNT',
      notesKey: 'AT_A_GLANCE.PORTION_DD_DETAILS_NOTES',
      totals: {
        labelKey: null,
        countProperty: 'collectableDDPaysCount',
        totalProperty: 'collectableDDPaysAmount'
      },
      subtotals: [],
      totalCountProperty: 'ddTotalItemsCount',
      isInvalid: false
    });
    this.portionProfiles.push({
      titleKey: 'AT_A_GLANCE.PORTION_INVALID_TITLE',
      countKey: 'AT_A_GLANCE.PORTION_INVALID_COUNT',
      totalCountKey: null,
      notesKey: null,
      totals: {
          labelKey: null,
          countProperty: 'ignoredPaysCount',
          totalProperty: 'ignoredPaysAmount'
      },
      subtotals: [{
          labelKey: 'AT_A_GLANCE.PORTION_INVALID_DETAILS_VOIDS',
          countProperty: 'ignoredVoidsCount',
          totalProperty: 'ignoredVoidsAmount'
        }, {
          labelKey: 'AT_A_GLANCE.PORTION_INVALID_DETAILS_INSTANT_CHECKS',
          countProperty: 'ignoredInstantChecksCount',
          totalProperty: 'ignoredInstantChecksAmount'
        }, {
          labelKey: 'AT_A_GLANCE.PORTION_INVALID_DETAILS_REVERSALS',
          countProperty: 'ignoredReversalsCount',
          totalProperty: 'ignoredReversalsAmount'
        }, {
          labelKey: 'AT_A_GLANCE.PORTION_INVALID_DETAILS_REJECTIONS',
          countProperty: 'ignoredNonCollectableCount',
          totalProperty: 'ignoredNonCollectableAmount'
        }, {
          labelKey: 'AT_A_GLANCE.PORTION_INVALID_DETAILS_OTHER',
          countProperty: 'ignoredOtherCount',
          totalProperty: 'ignoredOtherAmount'
        }],
      totalCountProperty: 'ignoredPaysCount',
      isInvalid: true
    });
  }

  get allExpanded(): boolean {
    return this._allExpanded;
  }

  @Input() set allExpanded(value: boolean) {
    if (value === this._allExpanded) {
      return;
    }

    this._allExpanded = value;
    this.sectionExpanded = value;
    this.stateChanged.emit();
  }

  get sectionExpanded(): boolean {
    return this._sectionExpanded;
  }

  @Input() set sectionExpanded(value: boolean) {
    if (value === this._sectionExpanded) {
      return;
    }

    this._sectionExpanded = value;
    this.stateChanged.emit();
  }
}
